import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import gql from 'graphql-tag';
import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { useMutation } from '@apollo/react-hooks';
import { AccountContext } from '../../../components/AccountContext';
import { notificationMessages } from '../../../i18n/common';

const messages = defineMessages({
  header: {
    id: 'account-hierarchy.confirm-delete-account-modal.header',
    defaultMessage: 'Warning',
  },
  firstParagraph: {
    id: 'account-hierarchy.confirm-delete-account-modal.first-paragraph',
    defaultMessage: 'Warning!',
  },
  secondParagraph: {
    id: 'account-hierarchy.confirm-delete-account-modal.second-paragraph',
    defaultMessage: 'You are about to delete Account {accountId} - {accountName}.',
  },
  thirdParagraph: {
    id: 'account-hierarchy.confirm-delete-account-modal.third-paragraph',
    defaultMessage: 'This action cannot be undone and all data will be lost.',
  },
  fourthParagraph: {
    id: 'account-hierarchy.confirm-delete-account-modal.fourth-paragraph',
    defaultMessage: 'Are you sure you want to do this?',
  },
  yes: { id: 'btn.lbl.yes', defaultMessage: 'Yes' },
  no: { id: 'btn.lbl.no', defaultMessage: 'No' },
});

const DELETE_ACCOUNT = gql`
  mutation($input: DeleteAccountInput!) {
    deleteAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

const CHECK_ACCOUNT_TO_DELETE = gql`
  mutation($input: DeleteAccountInput!) {
    checkAccountHierarchyBeforeDeletion(input: $input) {
      id
      displayName
      type
      ownerFullName
      ownerEmail
    }
  }
`;

const ConfirmDeleteAccountDialog = ({
  accountToDelete,
  hide,
  setAccountToDelete,
  setAccountsInHierarchyToDelete,
  fetchData,
}) => {
  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const [checkAccountToDeleteMutation] = useMutation(CHECK_ACCOUNT_TO_DELETE);
  const [deleteAccountMutation, { loading }] = useMutation(DELETE_ACCOUNT);

  const header = <div className="dashboard-panel-header">{formatMessage(messages.header)}</div>;
  const submitButtonLabel = loading ? (
    <span>
      <i className="pi pi-spin pi-spinner" />
    </span>
  ) : (
    <span>{formatMessage(messages.yes)}</span>
  );

  const handleClick = async () => {
    try {
      const result = await checkAccountToDeleteMutation({
        variables: {
          input: {
            sourceAccountId: currentAccount,
            targetAccountId: accountToDelete.id,
          },
        },
      });

      const accountsInHierarchyToDelete = result.data.checkAccountHierarchyBeforeDeletion;

      if (accountsInHierarchyToDelete.length === 0) {
        deleteAccountMutation({
          variables: {
            input: {
              sourceAccountId: currentAccount,
              targetAccountId: accountToDelete.id,
            },
          },
        }).then(() => {
          fetchData();
          hide();
          setAccountToDelete(null);

          growl.show({ severity: 'success', detail: formatMessage(notificationMessages.success) });
        });
      } else {
        hide();
        setAccountsInHierarchyToDelete(result.data.checkAccountHierarchyBeforeDeletion);
      }
    } catch (error) {
      growl.show({ severity: 'error', detail: error.message, sticky: true });
    }
  };

  return (
    <Dialog visible onHide={hide} header={header} modal>
      <div className="center-aligned-instructions">
        <p>{formatMessage(messages.firstParagraph)}</p>
        <p>
          {formatMessage(messages.secondParagraph, {
            accountId: accountToDelete.id,
            accountName: accountToDelete.brandDisplayName,
          })}
        </p>
        <p>{formatMessage(messages.thirdParagraph)}</p>
        <p>{formatMessage(messages.fourthParagraph)}</p>
      </div>
      <div className="mt20 flex-center">
        <Button
          type="button"
          className="p-button-primary"
          label={submitButtonLabel}
          disabled={loading}
          onClick={handleClick}
        />
        <Button
          type="button"
          className="p-button-secondary"
          label={formatMessage(messages.no)}
          disabled={loading}
          onClick={hide}
        />
      </div>
    </Dialog>
  );
};

export default ConfirmDeleteAccountDialog;
